import { isBrowser } from "../lib/env";
/**
 * Admin-mode runtime. Activates only when the page is loaded inside an iframe
 * AND a parent on an allow-listed origin sends an `admin-handshake` message.
 *
 * Once active:
 *   - Sets `data-admin-mode="on"` on documentElement (the CSS rule in index.css
 *     does the visual outline work).
 *   - Adds a single delegated click handler that intercepts clicks on any
 *     [data-edit-id] element and posts `{ type: "se:select", id }` to parent.
 *   - Handles `apply-draft-field` messages by patching the DOM directly:
 *     text fields → `el.textContent`, image fields → `el.src`. This provides
 *     live preview without React re-renders (deliberate Phase 4 simplification).
 */

interface HandshakeMessage {
  type: "admin-handshake";
  parentOrigin: string;
}

interface SelectMessage {
  type: "se:select";
  id: string;
}

interface ApplyDraftFieldMessage {
  type: "apply-draft-field";
  id: string;
  kind: "text" | "image-src";
  value: string;
}

interface ScrollToMessage {
  type: "scroll-to";
  id: string;
}

const ALLOWED_PARENT_ORIGINS = new Set([
  "http://localhost:3200",
  "https://se-admin.pages.dev",
  "https://admin.sadafal-education.com",
]);

export function maybeStartAdminMode(): void {
  if (!isBrowser()) {
    return;
  }
  if (window.parent === window) {
    return;
  }

  let parentOrigin: string | null = null;

  function onMessage(event: MessageEvent) {
    if (!ALLOWED_PARENT_ORIGINS.has(event.origin)) {
      return;
    }
    // SAFETY: messages are accepted only from ALLOWED_PARENT_ORIGINS, i.e. our own admin app, which posts these shapes; every field is treated as optional.
    const data = event.data as
      | Partial<HandshakeMessage | ApplyDraftFieldMessage | ScrollToMessage>
      | undefined;
    if (!data || data.type === undefined) {
      return;
    }
    if (data.type === "admin-handshake") {
      if (data.parentOrigin === event.origin) {
        parentOrigin = event.origin;
        activate();
      }
      return;
    }
    if (data.type === "apply-draft-field") {
      // SAFETY: the discriminant selected this message kind; applyField treats every field as optional.
      applyField(data as ApplyDraftFieldMessage);
      return;
    }
    if (data.type === "scroll-to") {
      // SAFETY: the discriminant selected this message kind; scrollTo tolerates a missing id.
      scrollTo((data as ScrollToMessage).id);
    }
  }

  function activate() {
    if (document.documentElement.dataset.adminMode === "on") {
      return;
    }
    document.documentElement.dataset.adminMode = "on";
    document.addEventListener("click", onClick, { capture: true });
  }

  function onClick(event: Event) {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const editable = target.closest("[data-edit-id]");
    if (!editable) {
      return;
    }
    const id = editable.getAttribute("data-edit-id");
    if (!id || !parentOrigin) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const msg: SelectMessage = { type: "se:select", id };
    window.parent.postMessage(msg, parentOrigin);
  }

  function applyField(msg: ApplyDraftFieldMessage) {
    const elements = document.querySelectorAll(
      `[data-edit-id="${cssEscape(msg.id)}"]`,
    );
    elements.forEach((el) => {
      if (msg.kind === "text") {
        el.textContent = msg.value;
      } else if (msg.kind === "image-src" && el instanceof HTMLImageElement) {
        el.src = msg.value;
      }
      flash(el);
    });
  }

  function scrollTo(id: string) {
    const el = document.querySelector(`[data-edit-id="${cssEscape(id)}"]`);
    if (!el) {
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    flash(el);
  }

  function flash(el: Element) {
    if (!(el instanceof HTMLElement)) {
      return;
    }
    el.dataset.editFlash = "on";
    window.setTimeout(() => {
      if (el.dataset.editFlash === "on") {
        delete el.dataset.editFlash;
      }
    }, 600);
  }

  window.addEventListener("message", onMessage);
}

function cssEscape(s: string): string {
  // Quoted attribute value — only need to escape backslashes and double quotes.
  return s.replaceAll("\\", "\\\\").replaceAll('"', '\\"');
}
